// frontend/src/pages/quotes/QuoteList.js
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchQuotes } from '../../store/slices/quoteSlice';
import {
    FileText, Plus, Search, X, ChevronRight, Ship, DollarSign,
    Calendar, User, Clock, CheckCircle, XCircle, AlertCircle,
    Loader2, LayoutGrid, List, BarChart3, Copy, Check, Filter,
} from 'lucide-react';
import Badge from '../../components/ui/Badge';
import usePersistedState from '../../hooks/usePersistedState';

// =========================================================
// CONSTANTS
// =========================================================
const STATUS_TABS = [
    { key: '', label: 'All', icon: LayoutGrid, dot: null },
    { key: 'draft', label: 'Drafts', icon: FileText, dot: 'bg-blue-500' },
    { key: 'submitted', label: 'Pending', icon: Clock, dot: 'bg-amber-500' },
    { key: 'approved', label: 'Approved', icon: CheckCircle, dot: 'bg-green-500' },
    { key: 'rejected', label: 'Rejected', icon: XCircle, dot: 'bg-red-500' },
];

const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'premium_high', label: 'Highest Premium' },
    { value: 'premium_low', label: 'Lowest Premium' },
];

const STATUS_STYLE = {
    approved: { pill: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', dot: 'bg-green-500' },
    rejected: { pill: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', dot: 'bg-red-500' },
    submitted: { pill: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
    draft: { pill: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', dot: 'bg-blue-500' },
};

// =========================================================
// MAIN COMPONENT
// =========================================================
export default function QuoteList() {
    const dispatch = useDispatch();
    const { items = [], loading, error } = useSelector((s) => s.quotes);

    const [searchParams, setSearchParams] = useSearchParams();
    const statusFilter = searchParams.get('status') || '';
    const urlSearch = searchParams.get('q') || '';

    const [searchInput, setSearchInput] = useState(urlSearch);
    const searchFieldRef = useRef(null);

    const [viewMode, setViewMode] = usePersistedState('quote_view_mode', 'list');
    const [sortBy, setSortBy] = useState('newest');

    const [copiedId, setCopiedId] = useState(null);

    // Debounce search into URL
    useEffect(() => {
        const t = setTimeout(() => {
            const next = new URLSearchParams(searchParams);
            if (searchInput.trim()) next.set('q', searchInput.trim());
            else next.delete('q');
            setSearchParams(next, { replace: true });
        }, 250);
        return () => clearTimeout(t);
    }, [searchInput, searchParams, setSearchParams]);

    // Keyboard: / focuses search, Esc clears
    useEffect(() => {
        const onKey = (e) => {
            const tag = document.activeElement?.tagName;
            const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

            if (e.key === '/' && !typing) {
                e.preventDefault();
                searchFieldRef.current?.focus();
            }
            if (e.key === 'Escape' && document.activeElement === searchFieldRef.current) {
                setSearchInput('');
                searchFieldRef.current?.blur();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // Fetch on status change
    useEffect(() => {
        dispatch(fetchQuotes(statusFilter || undefined));
    }, [dispatch, statusFilter]);

    const handleFilter = useCallback((status) => {
        const next = new URLSearchParams(searchParams);
        if (status) next.set('status', status);
        else next.delete('status');
        setSearchParams(next);
    }, [searchParams, setSearchParams]);

    const clearSearch = () => {
        setSearchInput('');
        const next = new URLSearchParams(searchParams);
        next.delete('q');
        setSearchParams(next, { replace: true });
    };

    const filteredQuotes = useMemo(() => {
        let rows = Array.isArray(items) ? [...items] : [];

        if (urlSearch) {
            const q = urlSearch.toLowerCase();
            rows = rows.filter((quote) =>
                String(quote.quote_number || '').toLowerCase().includes(q) ||
                String(quote.vessel_name || '').toLowerCase().includes(q) ||
                String(quote.insured_name || '').toLowerCase().includes(q) ||
                String(quote.created_by_name || '').toLowerCase().includes(q) ||
                String(quote.from_location || '').toLowerCase().includes(q) ||
                String(quote.to_location || '').toLowerCase().includes(q)
            );
        }

        switch (sortBy) {
            case 'newest':
                rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case 'oldest':
                rows.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                break;
            case 'premium_high':
                rows.sort((a, b) => Number(b.total_premium || 0) - Number(a.total_premium || 0));
                break;
            case 'premium_low':
                rows.sort((a, b) => Number(a.total_premium || 0) - Number(b.total_premium || 0));
                break;
            default:
                break;
        }

        return rows;
    }, [items, urlSearch, sortBy]);

    const stats = useMemo(() => ({
        total: items?.length || 0,
        submitted: items?.filter((q) => q.status === 'submitted').length || 0,
        approved: items?.filter((q) => q.status === 'approved').length || 0,
        rejected: items?.filter((q) => q.status === 'rejected').length || 0,
        draft: items?.filter((q) => q.status === 'draft').length || 0,
    }), [items]);

    const handleCopyRef = (e, ref) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(ref).then(() => {
            setCopiedId(ref);
            setTimeout(() => setCopiedId(null), 1500);
        }).catch(() => { /* ignore */ });
    };

    const hasActiveFilters = Boolean(statusFilter || urlSearch);

    if (loading && (!items || items.length === 0)) return <SkeletonLoader />;

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center max-w-md p-8">
                    <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                        Failed to Load Quotes
                    </h3>
                    <p className="text-gray-500 dark:text-slate-400">{error}</p>
                    <button
                        onClick={() => dispatch(fetchQuotes(statusFilter || undefined))}
                        className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-brand-600 text-white rounded-lg font-medium hover:bg-brand-700 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2"
                    >
                        <Loader2 className="w-4 h-4" />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

                {/* HEADER */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-brand-500/10 dark:bg-brand-500/20 rounded-lg">
                                <FileText className="w-6 h-6 text-brand-600 dark:text-green-400" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-extrabold text-gray-800 dark:text-white">
                                    Mayfair Quotations
                                </h1>
                                <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
                                    {filteredQuotes.length} quote{filteredQuotes.length !== 1 ? 's' : ''} found
                                    {hasActiveFilters && items.length !== filteredQuotes.length && (
                                        <> <span className="text-gray-400">of {items.length}</span></>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                    <Link
                        to="/quotes/new"
                        className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2"
                    >
                        <Plus className="w-5 h-5" />
                        New Quote
                    </Link>
                </div>

                {/* STATS */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <StatCard label="Total" value={stats.total} icon={BarChart3} color="bg-brand-600" onClick={() => handleFilter('')} active={!statusFilter} />
                    <StatCard label="Pending" value={stats.submitted} icon={Clock} color="bg-amber-500" onClick={() => handleFilter('submitted')} active={statusFilter === 'submitted'} />
                    <StatCard label="Approved" value={stats.approved} icon={CheckCircle} color="bg-green-500" onClick={() => handleFilter('approved')} active={statusFilter === 'approved'} />
                    <StatCard label="Rejected" value={stats.rejected} icon={XCircle} color="bg-red-500" onClick={() => handleFilter('rejected')} active={statusFilter === 'rejected'} />
                    <StatCard label="Drafts" value={stats.draft} icon={FileText} color="bg-blue-500" onClick={() => handleFilter('draft')} active={statusFilter === 'draft'} />
                </div>

                {/* FILTER BAR */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-4 space-y-3">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Filter by status">
                            {STATUS_TABS.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = statusFilter === tab.key;
                                const count = tab.key === '' ? stats.total : stats[tab.key] || 0;
                                return (
                                    <button
                                        key={tab.key || 'all'}
                                        onClick={() => handleFilter(tab.key)}
                                        role="tab"
                                        aria-selected={isActive}
                                        className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-brand-500/40 ${isActive
                                                ? 'bg-brand-600 text-white shadow-lg shadow-brand-600/20'
                                                : 'text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                                            }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {tab.label}
                                        {count > 0 && (
                                            <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-gray-100 dark:bg-slate-700'
                                                }`}>
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                <input
                                    ref={searchFieldRef}
                                    type="text"
                                    placeholder="Search quotes by number, vessel, or client..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    className="w-full pl-10 pr-10 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                                />
                                {searchInput && (
                                    <button
                                        type="button"
                                        onClick={clearSearch}
                                        aria-label="Clear search"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                                {!searchInput && (
                                    <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-block text-[10px] px-1.5 py-0.5 bg-white dark:bg-slate-600 text-gray-400 dark:text-slate-300 rounded font-mono border border-gray-200 dark:border-slate-500 pointer-events-none">
                                        /
                                    </kbd>
                                )}
                            </div>

                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                aria-label="Sort quotes"
                                className="px-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                            >
                                {SORT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>

                            <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('list')}
                                    aria-label="List view"
                                    aria-pressed={viewMode === 'list'}
                                    className={`p-1.5 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40 ${viewMode === 'list'
                                            ? 'bg-white dark:bg-slate-600 text-brand-600 dark:text-green-400 shadow-sm'
                                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <List className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('grid')}
                                    aria-label="Grid view"
                                    aria-pressed={viewMode === 'grid'}
                                    className={`p-1.5 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40 ${viewMode === 'grid'
                                            ? 'bg-white dark:bg-slate-600 text-brand-600 dark:text-green-400 shadow-sm'
                                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <LayoutGrid className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 dark:border-slate-700">
                            <Filter className="w-3.5 h-3.5 text-gray-400" />
                            {statusFilter && (
                                <FilterChip
                                    label={`Status: ${statusFilter}`}
                                    onClear={() => handleFilter('')}
                                />
                            )}
                            {urlSearch && (
                                <FilterChip
                                    label={`Search: "${urlSearch}"`}
                                    onClear={clearSearch}
                                />
                            )}
                            <button
                                onClick={() => {
                                    setSearchInput('');
                                    handleFilter('');
                                }}
                                className="text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-gray-200 underline underline-offset-2 ml-1"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* EMPTY STATE */}
                {filteredQuotes.length === 0 && (
                    <EmptyState
                        searchTerm={urlSearch}
                        statusFilter={statusFilter}
                        hasAny={items.length > 0}
                        onClearSearch={clearSearch}
                        onClearFilter={() => handleFilter('')}
                    />
                )}

                {/* LIST */}
                {filteredQuotes.length > 0 && (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-3'}>
                        {filteredQuotes.map((quote) => (
                            viewMode === 'grid' ? (
                                <QuoteGridCard
                                    key={quote.id}
                                    quote={quote}
                                    copied={copiedId === quote.quote_number}
                                    onCopy={(e) => handleCopyRef(e, quote.quote_number)}
                                />
                            ) : (
                                <QuoteListItem
                                    key={quote.id}
                                    quote={quote}
                                    copied={copiedId === quote.quote_number}
                                    onCopy={(e) => handleCopyRef(e, quote.quote_number)}
                                />
                            )
                        ))}
                    </div>
                )}

                {filteredQuotes.length > 0 && (
                    <p className="text-center text-xs text-gray-400 dark:text-slate-500 pt-2">
                        Showing {filteredQuotes.length} of {items.length} quote{items.length === 1 ? '' : 's'}
                    </p>
                )}
            </div>
        </div>
    );
}

// =========================================================
// SUB-COMPONENTS
// =========================================================

function StatCard({ label, value, icon: Icon, color, onClick, active }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-500/40 ${active
                    ? 'border-brand-500 dark:border-green-400 ring-2 ring-brand-500/20'
                    : 'border-gray-200 dark:border-slate-700'
                }`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-white mt-0.5">{value}</p>
                </div>
                <div className={`p-2 rounded-lg ${color} text-white`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
        </button>
    );
}

function FilterChip({ label, onClear }) {
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-brand-500/10 dark:bg-brand-500/20 text-brand-700 dark:text-green-400 border border-brand-500/20">
            {label}
            <button
                type="button"
                onClick={onClear}
                aria-label={`Clear ${label}`}
                className="hover:bg-brand-500/20 rounded-full p-0.5 transition-colors"
            >
                <X className="w-3 h-3" />
            </button>
        </span>
    );
}

function CopyRefButton({ copied, onCopy }) {
    return (
        <button
            type="button"
            onClick={onCopy}
            aria-label="Copy reference number"
            title={copied ? 'Copied!' : 'Copy reference'}
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded-lg text-gray-400 hover:text-brand-600 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        >
            {copied ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
                <Copy className="w-3.5 h-3.5" />
            )}
        </button>
    );
}

function QuoteListItem({ quote, copied, onCopy }) {
    const style = STATUS_STYLE[quote.status] || STATUS_STYLE.draft;
    const statusLabel = quote.status
        ? quote.status.charAt(0).toUpperCase() + quote.status.slice(1)
        : 'Unknown';

    return (
        <Link
            to={`/quotes/${quote.id}`}
            className="group block bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-lg hover:border-brand-500/30 dark:hover:border-green-400/30 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-bold text-gray-800 dark:text-white text-lg">
                            {quote.quote_number}
                        </h3>
                        <CopyRefButton copied={copied} onCopy={onCopy} />
                        <Badge status={quote.status} />
                        {quote.auto_approved && (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">
                                <CheckCircle className="w-3 h-3" />
                                Auto-Approved
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-slate-400">
                        <span className="flex items-center gap-1.5">
                            <Ship className="w-4 h-4 text-brand-600 dark:text-green-400" />
                            <span className="truncate max-w-[200px]">{quote.vessel_name || 'N/A'}</span>
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-slate-300">
                            <DollarSign className="w-4 h-4 text-brand-600 dark:text-green-400" />
                            {quote.currency === 'KES' ? 'KSh ' : '$'}
                            {Number(quote.total_premium || quote.total_amount || 0).toLocaleString()}
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span className="flex items-center gap-1.5">
                            <User className="w-4 h-4" />
                            <span className="truncate max-w-[140px]">{quote.created_by_name || 'N/A'}</span>
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            {new Date(quote.created_at).toLocaleDateString('en-KE', {
                                year: 'numeric', month: 'short', day: 'numeric',
                            })}
                        </span>
                    </div>
                    {quote.interests_insured && (
                        <p className="text-xs text-gray-400 dark:text-slate-500 mt-1.5 truncate max-w-md">
                            {quote.interests_insured}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${style.pill}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        {statusLabel}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-brand-600 dark:group-hover:text-green-400 transition-colors" />
                </div>
            </div>
        </Link>
    );
}

function QuoteGridCard({ quote, copied, onCopy }) {
    const style = STATUS_STYLE[quote.status] || STATUS_STYLE.draft;
    const statusLabel = quote.status
        ? quote.status.charAt(0).toUpperCase() + quote.status.slice(1)
        : 'Unknown';

    return (
        <Link
            to={`/quotes/${quote.id}`}
            className="group block bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-lg hover:border-brand-500/30 dark:hover:border-green-400/30 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        >
            <div className="flex items-start justify-between mb-3 gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-800 dark:text-white text-lg truncate">
                            {quote.quote_number}
                        </h3>
                        <CopyRefButton copied={copied} onCopy={onCopy} />
                    </div>
                    <div className="mt-1">
                        <Badge status={quote.status} />
                    </div>
                </div>
                <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider">Premium</p>
                    <p className="font-bold text-brand-600 dark:text-green-400">
                        {quote.currency === 'KES' ? 'KSh ' : '$'}
                        {Number(quote.total_premium || quote.total_amount || 0).toLocaleString()}
                    </p>
                </div>
            </div>

            <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600 dark:text-slate-300">
                    <Ship className="w-4 h-4 text-brand-600 dark:text-green-400 flex-shrink-0" />
                    <span className="truncate">{quote.vessel_name || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 dark:text-slate-300">
                    <User className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{quote.created_by_name || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 dark:text-slate-300">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>{new Date(quote.created_at).toLocaleDateString()}</span>
                </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1.5 ${style.pill}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                    {statusLabel}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-600 dark:group-hover:text-green-400 transition-colors" />
            </div>
        </Link>
    );
}

function EmptyState({ searchTerm, statusFilter, hasAny, onClearSearch, onClearFilter }) {
    const filtered = Boolean(searchTerm || statusFilter);
    return (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gray-100 dark:bg-slate-700 mb-6">
                <FileText className="w-12 h-12 text-gray-300 dark:text-slate-500" />
            </div>

            {filtered ? (
                <>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                        No matching quotes
                    </h3>
                    <p className="text-gray-500 dark:text-slate-400 max-w-sm mx-auto mb-6">
                        {searchTerm
                            ? <>No quotes match your search for <strong>"{searchTerm}"</strong></>
                            : <>No <strong>{statusFilter}</strong> quotes found.</>
                        }
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {searchTerm && (
                            <button
                                onClick={onClearSearch}
                                className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                        {statusFilter && (
                            <button
                                onClick={onClearFilter}
                                className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                            >
                                Show all statuses
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                        {hasAny ? 'No quotes match your view' : 'No quotes yet'}
                    </h3>
                    <p className="text-gray-500 dark:text-slate-400 max-w-sm mx-auto mb-6">
                        {hasAny
                            ? 'Try a different filter to see more.'
                            : 'Create your first marine insurance quote to get started.'}
                    </p>
                    <Link
                        to="/quotes/new"
                        className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl"
                    >
                        <Plus className="w-5 h-5" />
                        Create Quote
                    </Link>
                </>
            )}
        </div>
    );
}

function SkeletonLoader() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6 animate-pulse">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-40" />
                        <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-48 mt-2" />
                    </div>
                    <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-xl w-32" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="h-20 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                    ))}
                </div>
                <div className="h-24 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-24 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                ))}
            </div>
        </div>
    );
}